import { CollectionName } from '@/shared/lib/sj-orm/types';
import { UnknownObject } from '@/shared/types/utility-types';
import { log } from '@/shared/utils/log';
import { UpdateCollectionsModalCollection } from '@/widgets/modals/containers/UpdateCollectionsModal/types';

import {
  MigratedMigrationCollectionItem,
  MigratedMigrationCollectionsData,
  OnUserActionRequiredFn,
} from '../../types';

import { RequiredFieldsMigrationConfig } from './helpers';

const hasId = (obj: UnknownObject): obj is { id: string } =>
  'id' in obj && typeof obj.id === 'string';

const getUserActionRequiredItems = (
  data: MigratedMigrationCollectionsData,
  requiredOptions: RequiredFieldsMigrationConfig,
): UpdateCollectionsModalCollection[] => {
  const updateRequiredRecord = Object.entries(requiredOptions).reduce<
    Partial<Record<CollectionName, UpdateCollectionsModalCollection>>
  >((acc, [_collectionName, fieldCheck]) => {
    const collectionName = _collectionName as CollectionName;

    const collection = data.get(collectionName);

    if (!collection) {
      log.error(`[MIGRATION ERROR]: ${collectionName} not found`);
      return acc;
    }

    collection.forEach((collectionItem) => {
      const fieldsToFill = fieldCheck(collectionItem);

      if (!fieldsToFill?.length) {
        return;
      }

      if (!hasId(collectionItem)) {
        return;
      }

      if (acc[collectionName]) {
        acc[collectionName]?.items.push({ data: collectionItem, updateFields: fieldsToFill });
      } else {
        acc[collectionName] = {
          items: [{ data: collectionItem, updateFields: fieldsToFill }],
          name: collectionName,
        };
      }
    });

    return acc;
  }, {});

  const updateRequiredList = Object.values(updateRequiredRecord).map((collection) => collection);

  return updateRequiredList;
};

export const getFieldsToFillHelper = ({
  item,
  requiredFields,
}: {
  item: MigratedMigrationCollectionItem;
  requiredFields: string[];
}) => {
  if (!requiredFields?.length) {
    return undefined;
  }

  const fieldsToFill = requiredFields.reduce<string[]>((acc, fieldName) => {
    const fieldValue = item[fieldName];

    if (requiredFields.includes(fieldName) && !fieldValue) {
      acc.push(fieldName);
    }

    return acc;
  }, []);

  if (!fieldsToFill.length) {
    return undefined;
  }

  return fieldsToFill;
};

export const requiredFieldsCheck = async ({
  onUserActionRequired,
  migratedData,
  config,
}: {
  onUserActionRequired: OnUserActionRequiredFn;
  migratedData: MigratedMigrationCollectionsData;
  config: RequiredFieldsMigrationConfig;
}) => {
  const collections = getUserActionRequiredItems(migratedData, config);

  if (!collections.length) {
    return migratedData;
  }

  const requiredFieldsMigrated = structuredClone(migratedData);

  const userUpdatedData = await onUserActionRequired({ collections: collections });

  userUpdatedData.collections.forEach((collection, collectionName) => {
    const migratedCollection = requiredFieldsMigrated.get(collectionName);

    if (!migratedCollection) {
      log.error(`[MIGRATION ERROR]: Collection ${collectionName} not found`);
      return;
    }

    collection.forEach((collectionItem) => {
      migratedCollection.set(collectionItem.id, collectionItem);
    });
  });

  return requiredFieldsMigrated;
};
